
import type { Track } from './types';

// INSTRUCTIONS:
// 1. The Project ID has been extracted from your link: 'fmszkpimrvgdjcuzqpvy'
// 2. Ensure your Supabase bucket is named 'audio' and is set to PUBLIC.
// 3. Upload files matching the IDs below (e.g. 'rain-gentle.mp3').

const SUPABASE_PROJECT_ID = "fmszkpimrvgdjcuzqpvy";
const SUPABASE_AUDIO_BASE_URL = `https://${SUPABASE_PROJECT_ID}.supabase.co/storage/v1/object/public/audio/`;

export const PLACEHOLDER_AUDIO = `${SUPABASE_AUDIO_BASE_URL}gentle-rain.mp3`;

const getAudioUrl = (filename: string) => {
  // If the base URL is set, we assume the file exists there.
  return SUPABASE_AUDIO_BASE_URL ? `${SUPABASE_AUDIO_BASE_URL}${filename}.mp3` : PLACEHOLDER_AUDIO;
};

export const CATEGORIES = [
  { id: 'rain', name: 'Rain', icon: '🌧', prompt: 'calming rain stories', premiumOnly: false },
  { id: 'forest', name: 'Forest', icon: '🌲', prompt: 'peaceful forest stories', premiumOnly: false },
  { id: 'ocean', name: 'Ocean', icon: '🌊', prompt: 'ocean soothing stories', premiumOnly: false },
  { id: 'cosmos', name: 'Cosmos', icon: '🌌', prompt: 'cosmic journeys', premiumOnly: false },
];

export const NARRATORS = [
    { id: 'Kore', name: 'Kore', description: 'Warm & Motherly' },
    { id: 'Puck', name: 'Puck', description: 'Soft & Gentle' },
    { id: 'Fenrir', name: 'Fenrir', description: 'Deep & Resonant' },
    { id: 'Aoede', name: 'Aoede', description: 'Elegant & Poised' },
];

export const TRACKS: Track[] = [
  // --- SOUNDSCAPES ---
  {
    id: "rain-gentle",
    title: "Gentle Rain",
    description: "Soft rainfall on leaves, perfect for drifting off to sleep.",
    category: "Soundscapes",
    type: "soundscapes",
    subcategory: "light-rain",
    duration: 60,
    isNew: true,
    coverColor: "#1e3a5f",
    isLoopable: true,
    audioUrl: getAudioUrl("gentle-rain"),
    image: "https://images.unsplash.com/photo-1515690292960-ef27d81223e7?auto=format&fit=crop&q=80&w=800"
  },
  {
    id: "rain-heavy",
    title: "Heavy Downpour",
    description: "Intense, rhythmic rain for deep sleep masking.",
    category: "Soundscapes",
    type: "soundscapes",
    subcategory: "heavy-rain",
    duration: 60,
    isNew: false,
    coverColor: "#0c4a6e",
    isLoopable: true,
    audioUrl: getAudioUrl("heavy-downpour"),
    image: "https://images.unsplash.com/photo-1534274988754-ca76ea65c40e?auto=format&fit=crop&q=80&w=800"
  },
  {
    id: "rain-window",
    title: "Rain on Window",
    description: "Raindrops tapping against the glass pane.",
    category: "Soundscapes",
    type: "soundscapes",
    subcategory: "rain-on-window",
    duration: 60,
    isNew: false,
    coverColor: "#1e3a5f",
    isLoopable: true,
    audioUrl: getAudioUrl("rain-on-window"),
    image: "https://images.unsplash.com/photo-1519692933481-e162a57d6721?auto=format&fit=crop&q=80&w=800"
  },
  {
    id: "thunder-distant",
    title: "Distant Thunder",
    description: "Rolling thunder far away in the night sky.",
    category: "Soundscapes",
    type: "soundscapes",
    subcategory: "thunderstorm",
    duration: 60,
    isNew: true,
    coverColor: "#2d3748",
    isLoopable: true,
    audioUrl: getAudioUrl("distant-thunder"),
    image: "https://images.unsplash.com/photo-1605727216801-e27ce1d0cc28?auto=format&fit=crop&q=80&w=800"
  },
  {
    id: "ocean-waves",
    title: "Ocean Waves",
    description: "Rhythmic waves crashing on a sandy shore.",
    category: "Soundscapes",
    type: "soundscapes",
    subcategory: "waves",
    duration: 60,
    isNew: false,
    coverColor: "#1a4d5c",
    isLoopable: true,
    audioUrl: getAudioUrl("ocean-waves"),
    image: "https://images.unsplash.com/photo-1505118380757-91f5f5632de0?auto=format&fit=crop&q=80&w=800"
  },
  {
    id: "underwater",
    title: "Underwater Drift",
    description: "Muffled, deep ocean sounds.",
    category: "Soundscapes",
    type: "soundscapes",
    subcategory: "underwater",
    duration: 60,
    isNew: false,
    coverColor: "#0e7490",
    isLoopable: true,
    audioUrl: getAudioUrl("underwater-drift"),
    image: "https://images.unsplash.com/photo-1682687220742-aba13b6e50ba?auto=format&fit=crop&q=80&w=800"
  },
  {
    id: "fireplace",
    title: "Crackling Fireplace",
    description: "Warm, woody crackles from a stone hearth.",
    category: "Soundscapes",
    type: "soundscapes",
    subcategory: "fireplace",
    duration: 60,
    isNew: true,
    coverColor: "#7c2d12",
    isLoopable: true,
    audioUrl: getAudioUrl("crackling-fireplace"),
    image: "https://images.unsplash.com/photo-1542202229-7d93c33f5d07?auto=format&fit=crop&q=80&w=800"
  },
  {
    id: "campfire",
    title: "Campfire Night",
    description: "Outdoor fire with crickets in the background.",
    category: "Soundscapes",
    type: "soundscapes",
    subcategory: "campfire",
    duration: 60,
    isNew: false,
    coverColor: "#92400e",
    isLoopable: true,
    audioUrl: getAudioUrl("campfire-night"),
    image: "https://images.unsplash.com/photo-1523987355523-c7b5b0dd90a7?auto=format&fit=crop&q=80&w=800"
  },
  {
    id: "birds-forest",
    title: "Forest Birds",
    description: "Morning birdsong in a lush forest.",
    category: "Soundscapes",
    type: "soundscapes",
    subcategory: "birds",
    duration: 60,
    isNew: false,
    coverColor: "#166534",
    isLoopable: true,
    audioUrl: getAudioUrl("forest-birds"),
    image: "https://images.unsplash.com/photo-1441974231531-c6227db76b6e?auto=format&fit=crop&q=80&w=800"
  },
  {
    id: "night-forest",
    title: "Night Forest",
    description: "Nocturnal ambience with crickets and wind.",
    category: "Soundscapes",
    type: "soundscapes",
    subcategory: "night-forest",
    duration: 60,
    isNew: false,
    coverColor: "#14532d",
    isLoopable: true,
    audioUrl: getAudioUrl("night-forest"),
    image: "https://images.unsplash.com/photo-1500382017468-9049fed747ef?auto=format&fit=crop&q=80&w=800"
  },
  {
    id: "stream",
    title: "Flowing Stream",
    description: "Water rushing over smooth river stones.",
    category: "Soundscapes",
    type: "soundscapes",
    subcategory: "stream",
    duration: 60,
    isNew: false,
    coverColor: "#0ea5e9",
    isLoopable: true,
    audioUrl: getAudioUrl("flowing-stream"),
    image: "https://images.unsplash.com/photo-1432405972618-c60b0225b8f9?auto=format&fit=crop&q=80&w=800"
  },
  {
    id: "wind-gentle",
    title: "Gentle Wind",
    description: "Soft breeze rustling through trees.",
    category: "Soundscapes",
    type: "soundscapes",
    subcategory: "gentle-breeze",
    duration: 60,
    isNew: false,
    coverColor: "#64748b",
    isLoopable: true,
    audioUrl: getAudioUrl("gentle-wind"),
    image: "https://images.unsplash.com/photo-1433838552652-f9a46b332c40?auto=format&fit=crop&q=80&w=800"
  },
  {
    id: "coffee-shop",
    title: "Coffee Shop",
    description: "Ambient chatter and clinking cups.",
    category: "Soundscapes",
    type: "soundscapes",
    subcategory: "coffee-shop",
    duration: 60,
    isNew: true,
    coverColor: "#78350f",
    isLoopable: true,
    audioUrl: getAudioUrl("coffee-shop"),
    image: "https://images.unsplash.com/photo-1509042239860-f550ce710b93?auto=format&fit=crop&q=80&w=800"
  },
  {
    id: "train-night",
    title: "Night Train",
    description: "Rhythmic clacking of a train on tracks.",
    category: "Soundscapes",
    type: "soundscapes",
    subcategory: "train",
    duration: 60,
    isNew: false,
    coverColor: "#334155",
    isLoopable: true,
    audioUrl: getAudioUrl("night-train"),
    image: "https://images.unsplash.com/photo-1474487548417-781cb71495f3?auto=format&fit=crop&q=80&w=800"
  },

  // --- WHITE NOISE & MORE ---
  {
    id: "white-noise",
    title: "Pure White Noise",
    description: "Consistent, static sound for blocking distractions.",
    category: "White Noise & More",
    type: "noise",
    subcategory: "white-noise",
    duration: 60,
    isNew: false,
    coverColor: "#374151",
    isLoopable: true,
    audioUrl: getAudioUrl("pure-white-noise"),
    image: "https://images.unsplash.com/photo-1550684848-fac1c5b4e853?auto=format&fit=crop&q=80&w=800"
  },
  {
    id: "pink-noise",
    title: "Pink Noise",
    description: "Balanced frequency noise, deeper than white noise.",
    category: "White Noise & More",
    type: "noise",
    subcategory: "pink-noise",
    duration: 60,
    isNew: false,
    coverColor: "#831843",
    isLoopable: true,
    audioUrl: getAudioUrl("pink-noise"),
    image: "https://images.unsplash.com/photo-1550684847-75bdda21cc95?auto=format&fit=crop&q=80&w=800"
  },
  {
    id: "brown-noise",
    title: "Brown Noise",
    description: "Deep, rumbling noise for relaxation.",
    category: "White Noise & More",
    type: "noise",
    subcategory: "brown-noise",
    duration: 60,
    isNew: true,
    coverColor: "#44403c",
    isLoopable: true,
    audioUrl: getAudioUrl("brown-noise"),
    image: "https://images.unsplash.com/photo-1627393165264-f6eb07127113?auto=format&fit=crop&q=80&w=800"
  },
  {
    id: "green-noise",
    title: "Green Noise",
    description: "Noise with nature-like frequencies.",
    category: "White Noise & More",
    type: "noise",
    subcategory: "green-noise",
    duration: 60,
    isNew: true,
    coverColor: "#14532d",
    isLoopable: true,
    audioUrl: getAudioUrl("green-noise"),
    image: "https://images.unsplash.com/photo-1518173946687-a4c8892bbd9f?auto=format&fit=crop&q=80&w=800"
  },
  {
    id: "fan",
    title: "Fan Sound",
    description: "Whirring of a bedroom fan.",
    category: "White Noise & More",
    type: "noise",
    subcategory: "fan",
    duration: 60,
    isNew: false,
    coverColor: "#1f2937",
    isLoopable: true,
    audioUrl: getAudioUrl("fan-sound"),
    image: "https://images.unsplash.com/photo-1618567845353-8d6333333333?auto=format&fit=crop&q=80&w=800"
  },
  {
    id: "ac",
    title: "Air Conditioner",
    description: "Hum of an AC unit.",
    category: "White Noise & More",
    type: "noise",
    subcategory: "air-conditioner",
    duration: 60,
    isNew: false,
    coverColor: "#475569",
    isLoopable: true,
    audioUrl: getAudioUrl("air-conditioner"),
    image: "https://images.unsplash.com/photo-1633519349893-27e1329c2933?auto=format&fit=crop&q=80&w=800"
  },
  {
    id: "static",
    title: "TV Static",
    description: "Classic fuzz from an old TV.",
    category: "White Noise & More",
    type: "noise",
    subcategory: "static",
    duration: 60,
    isNew: false,
    coverColor: "#525252",
    isLoopable: true,
    audioUrl: getAudioUrl("tv-static"),
    image: "https://images.unsplash.com/photo-1585314062604-1a357de8b000?auto=format&fit=crop&q=80&w=800"
  },

  // --- MUSIC ---
  {
    id: "ambient-drift",
    title: "Ambient Drift",
    description: "Slow, evolving ambient textures.",
    category: "Music",
    type: "music",
    subcategory: "ambient",
    duration: 45,
    isNew: true,
    coverColor: "#312e81",
    isLoopable: true,
    audioUrl: getAudioUrl("ambient-drift"),
    image: "https://images.unsplash.com/photo-1511379938547-71f0d36bcb63?auto=format&fit=crop&q=80&w=800"
  },
  {
    id: "deep-drone",
    title: "Deep Drone",
    description: "Low frequency drone for meditation.",
    category: "Music",
    type: "music",
    subcategory: "drone",
    duration: 60,
    isNew: false,
    coverColor: "#172554",
    isLoopable: true,
    audioUrl: getAudioUrl("deep-drone"),
    image: "https://images.unsplash.com/photo-1419242902214-272b3f66ee7a?auto=format&fit=crop&q=80&w=800"
  },
  {
    id: "sleep-piano",
    title: "Sleep Piano",
    description: "Minimalist piano melodies.",
    category: "Music",
    type: "music",
    subcategory: "piano",
    duration: 40,
    isNew: false,
    coverColor: "#1e1b4b",
    isLoopable: true,
    audioUrl: getAudioUrl("sleep-piano"),
    image: "https://images.unsplash.com/photo-1552422535-c45813c61732?auto=format&fit=crop&q=80&w=800"
  },
  {
    id: "lofi-dreams",
    title: "Lo-fi Dreams",
    description: "Chill beats to relax to.",
    category: "Music",
    type: "music",
    subcategory: "lo-fi",
    duration: 45,
    isNew: false,
    coverColor: "#3f3f46",
    isLoopable: true,
    audioUrl: getAudioUrl("lo-fi-dreams"),
    image: "https://images.unsplash.com/photo-1516280440614-6697288d5d38?auto=format&fit=crop&q=80&w=800"
  },
  {
    id: "theta-waves",
    title: "Theta Waves",
    description: "Binaural beats for deep relaxation.",
    category: "Music",
    type: "music",
    subcategory: "binaural-beats",
    duration: 30,
    isNew: true,
    coverColor: "#0f766e",
    isLoopable: true,
    audioUrl: getAudioUrl("theta-waves"),
    image: "https://images.unsplash.com/photo-1518609878373-06d740f60d8b?auto=format&fit=crop&q=80&w=800"
  },
  {
    id: "delta-sleep",
    title: "Delta Sleep",
    description: "Deep sleep inducing frequencies.",
    category: "Music",
    type: "music",
    subcategory: "binaural-beats",
    duration: 60,
    isNew: false,
    coverColor: "#115e59",
    isLoopable: true,
    audioUrl: getAudioUrl("delta-sleep"),
    image: "https://images.unsplash.com/photo-1506126613408-eca07ce68773?auto=format&fit=crop&q=80&w=800"
  },
  {
    id: "singing-bowls",
    title: "Meditation Bowls",
    description: "Resonant Tibetan singing bowls.",
    category: "Music",
    type: "music",
    subcategory: "meditation",
    duration: 35,
    isNew: false,
    coverColor: "#b45309",
    isLoopable: true,
    audioUrl: getAudioUrl("meditation-bowls"),
    image: "https://images.unsplash.com/photo-1593697821252-0c9137d9fc45?auto=format&fit=crop&q=80&w=800"
  },
  {
    id: "calm-guitar",
    title: "Calm Guitar",
    description: "Gentle acoustic guitar picking.",
    category: "Music",
    type: "music",
    subcategory: "meditation",
    duration: 40,
    isNew: false,
    coverColor: "#7c2d12",
    isLoopable: true,
    audioUrl: getAudioUrl("calm-guitar"),
    image: "https://images.unsplash.com/photo-1465847899084-d164df4dedc6?auto=format&fit=crop&q=80&w=800"
  },

  // --- STORIES ---
  {
    id: "history-rome",
    title: "The Sleepy History of Rome",
    description: "A monotone journey through ancient Roman architecture. Deliberately dull.",
    category: "Stories",
    type: "stories",
    subcategory: "boring-history",
    duration: 24,
    isNew: false,
    coverColor: "#78716c",
    isLoopable: false,
    audioUrl: getAudioUrl("story-rome"),
    image: "https://images.unsplash.com/photo-1548560781-a7a07d6346bd?auto=format&fit=crop&q=80&w=800"
  },
  {
    id: "history-bricks",
    title: "The History of Brick Making",
    description: "The incredibly dull process of making bricks throughout history.",
    category: "Stories",
    type: "stories",
    subcategory: "boring-history",
    duration: 18,
    isNew: false,
    coverColor: "#57534e",
    isLoopable: false,
    audioUrl: getAudioUrl("story-bricks"),
    image: "https://images.unsplash.com/photo-1589982424914-8aa7285d8830?auto=format&fit=crop&q=80&w=800"
  },
  {
    id: "rainy-cottage",
    title: "Rainy Cottage Morning",
    description: "A cosy morning in a countryside cottage as rain patters on the window.",
    category: "Stories",
    type: "stories",
    subcategory: "cosy",
    duration: 15,
    isNew: true,
    coverColor: "#365314",
    isLoopable: false,
    audioUrl: getAudioUrl("story-cottage"),
    image: "https://images.unsplash.com/photo-1494526585095-c41746248156?auto=format&fit=crop&q=80&w=800"
  },
  {
    id: "beekeeper",
    title: "The Beekeeper's Garden",
    description: "A gentle walk through a buzzing garden in mid-summer.",
    category: "Stories",
    type: "stories",
    subcategory: "cosy",
    duration: 21,
    isNew: false,
    coverColor: "#a16207",
    isLoopable: false,
    audioUrl: getAudioUrl("story-beekeeper"),
    image: "https://images.unsplash.com/photo-1476317872727-4f420d4d536b?auto=format&fit=crop&q=80&w=800"
  },
  {
    id: "enchanted-library",
    title: "The Enchanted Library",
    description: "Wander through an endless library of magical books in a dreamlike realm.",
    category: "Stories",
    type: "stories",
    subcategory: "fantasy",
    duration: 28,
    isNew: false,
    coverColor: "#4c1d95",
    isLoopable: false,
    audioUrl: getAudioUrl("story-library"),
    image: "https://images.unsplash.com/photo-1481627834876-b7833e8f5570?auto=format&fit=crop&q=80&w=800"
  },
  {
    id: "cloud-shepherd",
    title: "The Cloud Shepherd",
    description: "Herding fluffy clouds across a twilight sky.",
    category: "Stories",
    type: "stories",
    subcategory: "fantasy",
    duration: 14,
    isNew: true,
    coverColor: "#6366f1",
    isLoopable: false,
    audioUrl: getAudioUrl("story-clouds"),
    image: "https://images.unsplash.com/photo-1594156596782-fa820516766d?auto=format&fit=crop&q=80&w=800"
  },
  {
    id: "velveteen-rabbit",
    title: "The Velveteen Rabbit",
    description: "A gentle retelling of the classic bedtime story about becoming real.",
    category: "Stories",
    type: "stories",
    subcategory: "bedtime-classics",
    duration: 22,
    isNew: false,
    coverColor: "#9d174d",
    isLoopable: false,
    audioUrl: getAudioUrl("story-rabbit"),
    image: "https://images.unsplash.com/photo-1516233758813-a38d31336e38?auto=format&fit=crop&q=80&w=800"
  },
  {
    id: "wind-willows",
    title: "The Wind in the Willows",
    description: "Riverbank adventures with Ratty and Mole, told slowly.",
    category: "Stories",
    type: "stories",
    subcategory: "bedtime-classics",
    duration: 26,
    isNew: false,
    coverColor: "#166534",
    isLoopable: false,
    audioUrl: getAudioUrl("story-willows"),
    image: "https://images.unsplash.com/photo-1447752875215-b2761acb3c5d?auto=format&fit=crop&q=80&w=800"
  },
  {
    id: "meadow-walk",
    title: "Walk Through the Meadow",
    description: "A guided journey through a sunlit meadow as evening approaches.",
    category: "Stories",
    type: "stories",
    subcategory: "sleep-journeys",
    duration: 12,
    isNew: true,
    coverColor: "#15803d",
    isLoopable: false,
    audioUrl: getAudioUrl("story-meadow"),
    image: "https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?auto=format&fit=crop&q=80&w=800"
  },
  {
    id: "moonlit-shore",
    title: "The Moonlit Shore",
    description: "A peaceful walk along a beach bathed in silver moonlight.",
    category: "Stories",
    type: "stories",
    subcategory: "sleep-journeys",
    duration: 19,
    isNew: false,
    coverColor: "#1e3a8a",
    isLoopable: false,
    audioUrl: getAudioUrl("story-shore"),
    image: "https://images.unsplash.com/photo-1478728073286-db190d3d8ce6?auto=format&fit=crop&q=80&w=800"
  }
];
